import { useLayoutEffect, useRef } from 'react';

import { isTabNavigation, rememberScroll, scrollOfPath } from './scrollMemory';

/**
 * Puts the page at its top before it's painted, and keeps it there while the content settles in (data, banners,
 * images move things on some phones), until the person scrolls or presses anything. Returns the cleanup.
 * 2.5 s, not 1: a flick on the page before still glides on an iPhone, Safari skips scroll calls while it does, and
 * the glide carried the new page down once the hold was over (a tab tapped mid-glide)
 */
export const holdPageTop = ({ ms = 2500 } = {}) => {
  window.scrollTo(0, 0);
  const until = performance.now() + ms;
  const stopEvents = ['wheel', 'touchstart', 'keydown', 'pointerdown'];
  let frame = 0;
  // Once the page can scroll: 1px down, then back up the next frame. Safari 26 can move a page only on the inside
  // (it says it's at the top, the screen shows where it was and taps land off target, WebKit bug 309542); a real
  // scroll brings the screen back in line, and a scroll to where the page already says it is doesn't
  let resync = 'waiting';
  const stop = () => cancelAnimationFrame(frame);
  const keepTop = () => {
    if (performance.now() > until) return;
    if (resync === 'down') {
      resync = 'done';
      window.scrollTo(0, 0);
    } else if (window.scrollY !== 0) {
      window.scrollTo(0, 0);
    } else if (resync === 'waiting' && document.documentElement.scrollHeight > window.innerHeight + 1) {
      resync = 'down';
      window.scrollTo(0, 1);
    }
    frame = requestAnimationFrame(keepTop);
  };
  frame = requestAnimationFrame(keepTop);
  stopEvents.forEach((type) => window.addEventListener(type, stop, { passive: true, once: true }));
  return () => {
    stop();
    stopEvents.forEach((type) => window.removeEventListener(type, stop));
  };
};

// Pages that keep their screens in the query: a new screen there is a new page for the scroll
const SCREEN_PARAMS = { '/diet': ['view', 'step'] };

const screenOf = ({ pathname, search }) => {
  const keys = SCREEN_PARAMS[pathname];
  if (!keys) return pathname;
  const params = new URLSearchParams(search);
  return `${pathname}?${keys.map((key) => params.get(key) || '').join('&')}`;
};

/**
 * A page coming back goes where it was left. It's drawn from memory, but what it holds can still land a moment later:
 * keep asking for about a second, until the page is tall enough, and stop when the person scrolls on their own.
 */
export const restoreScroll = (y) => {
  let frames = 0;
  let id = null;
  const stopEvents = ['wheel', 'touchstart'];
  const stop = () => {
    if (id !== null) cancelAnimationFrame(id);
    id = null;
    stopEvents.forEach((type) => window.removeEventListener(type, stop));
  };
  const step = () => {
    id = null;
    window.scrollTo(0, y);
    frames += 1;
    if (Math.abs(window.scrollY - y) <= 1 || frames >= 60) stop();
    else id = requestAnimationFrame(step);
  };
  stopEvents.forEach((type) => window.addEventListener(type, stop, { passive: true }));
  step();
};

/**
 * A new page (or a new screen of one) goes to the top the moment it's asked for, while the old page is still on
 * screen, before the new one is drawn: Safari passes that scroll on to the screen. Scrolled only after the new,
 * shorter page was drawn, Safari had already pulled the page up on its own, inside only, and the screen stayed
 * mid-page with taps landing off target (the tab bar seemed dead). Back, forward and a tab with a place of its own go
 * back there instead (ScrollToTop). The router calls this before React draws the new state, so it's also where the page
 * being left is remembered: the position comes from the scroll events, since a browser's own Back can move the window
 * first. ScrollToTop (App.jsx) and usePageTopOn hold the top after.
 */
export const scrollTopOnNavigation = (router) => {
  let shown = screenOf(router.state.location);
  let entry = router.state.location;
  let lastY = window.scrollY;
  window.addEventListener(
    'scroll',
    () => {
      lastY = window.scrollY;
    },
    { passive: true },
  );
  return router.subscribe((state) => {
    if (state.location.key !== entry.key) {
      rememberScroll(entry.key, entry.pathname, lastY);
      entry = state.location;
    }
    const next = screenOf(state.location);
    if (next === shown) return;
    shown = next;
    if (state.historyAction === 'POP') return;
    if (isTabNavigation(state.location.state) && scrollOfPath(state.location.pathname)) return;
    window.scrollTo(0, 0);
  });
};

/**
 * A screen of its own inside one page (a step, a view on ?view=, the import's stages): each new one starts at the
 * top like a new page, held there after it's drawn (the query's screens also go up before, scrollTopOnNavigation).
 */
export const usePageTopOn = (screenKey) => {
  const isFirst = useRef(true);
  useLayoutEffect(() => {
    // The page's first screen is the router's (ScrollToTop in App.jsx)
    if (isFirst.current) {
      isFirst.current = false;
      return undefined;
    }
    return holdPageTop();
  }, [screenKey]);
};
